import Phaser from 'phaser';
import { COLORS, FONTS, FORSKUDD_PER_TIER, GAME_HEIGHT, GAME_WIDTH, MODAL_BREDDE, TIER_COLORS, fontStr } from '@/config';
import { gameState } from '@/state/store';
import type { Manuscript } from '@/data/types';
import type { GameStateData } from '@/state/GameState';
import { konkurrentKatalog } from '@/data/konkurrenter';
import { sfx } from '@/systems/sfx';
import { animateModalGlide } from '@/ui/modalGlide';

const SHEET_W = MODAL_BREDDE;
const CARD_WIDTH = MODAL_BREDDE - 60;
const CARD_PADDING = 32;
const CARD_GAP = 28;

export class InboxModal {
  private layer?: Phaser.GameObjects.Container;
  private unsubscribe?: () => void;
  private confirmLayer?: Phaser.GameObjects.Container;
  private førsteKortBounds: { x: number; y: number; w: number; h: number } | null = null;
  private førsteTilbyKnappBounds: { x: number; y: number; w: number; h: number } | null = null;

  constructor(private scene: Phaser.Scene) {}

  /**
   * Returnerer en bounding-rect for første «Tilby kontrakt»-knapp, dersom
   * innboksen er åpen og det finnes minst ett manus uten konkurrent-by.
   */
  førsteTilbyRect(): { x: number; y: number; w: number; h: number } | null {
    if (!this.layer) return null;
    return this.førsteTilbyKnappBounds;
  }

  førsteKortRect(): { x: number; y: number; w: number; h: number } | null {
    if (!this.layer) return null;
    return this.førsteKortBounds;
  }

  open(): void {
    if (this.layer) return;
    this.layer = this.scene.add.container(0, 0).setDepth(100);

    const backdrop = this.scene.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.55)
      .setOrigin(0, 0)
      .setInteractive();
    this.layer.add(backdrop);

    this.render();
    animateModalGlide(this.scene, this.layer);
    this.unsubscribe = gameState.subscribe(() => this.render());
  }

  close(): void {
    this.unsubscribe?.();
    this.unsubscribe = undefined;
    this.closeConfirm();
    this.layer?.destroy(true);
    this.layer = undefined;
  }

  get isOpen(): boolean {
    return !!this.layer;
  }

  private render(): void {
    if (!this.layer) return;
    while (this.layer.list.length > 1) {
      const last = this.layer.list[this.layer.list.length - 1];
      this.layer.remove(last, true);
    }

    const snap = gameState.snapshot;

    const sheetMarginX = (GAME_WIDTH - SHEET_W) / 2;
    const sheet = this.scene.add
      .rectangle(sheetMarginX, 96, SHEET_W, GAME_HEIGHT - 192, COLORS.beige, 0.98)
      .setOrigin(0, 0)
      .setStrokeStyle(3, COLORS.wine, 0.6);
    this.layer.add(sheet);

    const headerY = 132;
    const header = this.scene.add
      .text(sheetMarginX + CARD_PADDING, headerY, `Innboks — Dag ${snap.dag}`, {
        fontFamily: FONTS.display,
        fontSize: fontStr(54),
        color: '#862736',
        wordWrap: { width: SHEET_W - CARD_PADDING * 2 - 110 },
      })
      .setOrigin(0, 0);
    this.layer.add(header);

    const subtitle = this.scene.add
      .text(
        sheetMarginX + CARD_PADDING,
        headerY + 92,
        `${snap.innboks.length} nye manuskripter venter`,
        {
          fontFamily: FONTS.body,
          fontSize: fontStr(24),
          color: '#3a2e22',
        },
      )
      .setOrigin(0, 0);
    this.layer.add(subtitle);

    const closeBtn = this.scene.add
      .text(sheetMarginX + SHEET_W - CARD_PADDING, headerY - 4, '×', {
        fontFamily: FONTS.display,
        fontSize: fontStr(72),
        color: '#1a1a1a',
      })
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true });
    closeBtn.on('pointerup', () => this.close());
    this.layer.add(closeBtn);

    const listX = sheetMarginX + (SHEET_W - CARD_WIDTH) / 2;
    let y = headerY + 180;

    this.førsteKortBounds = null;
    this.førsteTilbyKnappBounds = null;
    if (snap.innboks.length === 0) {
      const emptyBox = this.scene.add
        .rectangle(listX, y, CARD_WIDTH, 220, COLORS.white, 0.9)
        .setOrigin(0, 0)
        .setStrokeStyle(2, COLORS.oakDark, 0.4);
      const emptyText = this.scene.add
        .text(
          listX + CARD_WIDTH / 2,
          y + 110,
          'Innboksen er tom.\nKlikk Neste dag for nye manus.',
          {
            fontFamily: FONTS.body,
            fontSize: fontStr(28),
            color: '#1a1a1a',
            align: 'center',
          },
        )
        .setOrigin(0.5);
      this.layer.add([emptyBox, emptyText]);
    } else {
      let førsteRegistrert = false;
      for (const manus of snap.innboks) {
        const card = this.buildCard(listX, y, manus, snap);
        this.layer.add(card);
        const bounds = card.getBounds();
        if (!førsteRegistrert) {
          this.førsteKortBounds = {
            x: listX,
            y,
            w: CARD_WIDTH,
            h: bounds.height,
          };
          // Tilby/Match-knappen er nederst til høyre i kortet
          if (manus.konkurrentBy) {
            const buttonsY = y + bounds.height - 92;
            const halvBredde = (CARD_WIDTH - CARD_PADDING * 2 - 16) / 2;
            this.førsteTilbyKnappBounds = {
              x: listX + CARD_PADDING,
              y: buttonsY,
              w: halvBredde,
              h: 72,
            };
          } else {
            const buttonsY = y + bounds.height - 92;
            const buttonWidth = (CARD_WIDTH - CARD_PADDING * 2 - 32) / 3;
            const knappX = listX + CARD_PADDING + (buttonWidth + 16) * 2;
            this.førsteTilbyKnappBounds = {
              x: knappX,
              y: buttonsY,
              w: buttonWidth,
              h: 72,
            };
          }
          førsteRegistrert = true;
        }
        y += bounds.height + CARD_GAP;
      }
    }

    const footer = this.scene.add
      .text(
        sheetMarginX + SHEET_W / 2,
        96 + (GAME_HEIGHT - 192) - 56,
        'Forskudd: D=20k · C=40k · B=80k · A=140k · S=220k',
        {
          fontFamily: FONTS.hud,
          fontSize: fontStr(20),
          color: '#3a2e22',
        },
      )
      .setOrigin(0.5, 0.5);
    this.layer.add(footer);
  }

  private buildCard(
    x: number,
    y: number,
    manus: Manuscript,
    snap: GameStateData,
  ): Phaser.GameObjects.Container {
    const container = this.scene.add.container(x, y);
    const harBy = !!manus.konkurrentBy;
    const fraForfatter = manus.fraForfatterId
      ? snap.authors[manus.fraForfatterId]
      : undefined;
    const harForfatterBanner = !!fraForfatter;
    let cardHeight = harBy ? 660 : 520;
    if (harForfatterBanner) cardHeight += 90;

    const bg = this.scene.add
      .rectangle(0, 0, CARD_WIDTH, cardHeight, COLORS.white, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.oakDark, 0.5);
    container.add(bg);

    let nestY = 28;

    if (harForfatterBanner && fraForfatter) {
      const banner = this.buildForfatterBanner(
        CARD_PADDING,
        nestY,
        CARD_WIDTH - CARD_PADDING * 2,
        fraForfatter.navn,
      );
      container.add(banner.container);
      nestY += banner.height + 16;
    }

    if (harBy && manus.konkurrentBy) {
      const banner = this.buildKonkurrentBanner(
        CARD_PADDING,
        nestY,
        CARD_WIDTH - CARD_PADDING * 2,
        manus.konkurrentBy.konkurrentId,
        manus.konkurrentBy.beloep,
      );
      container.add(banner.container);
      nestY += banner.height + 16;
    }

    const title = this.scene.add
      .text(CARD_PADDING, nestY, manus.title, {
        fontFamily: FONTS.display,
        fontSize: fontStr(40),
        color: '#1a1a1a',
        wordWrap: { width: CARD_WIDTH - CARD_PADDING * 2 },
      })
      .setOrigin(0, 0);
    container.add(title);
    nestY += title.height + 16;

    const erPseudonym = !manus.author || manus.author.trim().toLowerCase() === 'ukjent forfatter';
    const navnVis = erPseudonym ? 'Pseudonym (foreløpig ukjent)' : manus.author;
    const author = this.scene.add
      .text(CARD_PADDING, nestY, navnVis, {
        fontFamily: FONTS.body,
        fontSize: fontStr(26),
        color: '#3a2e22',
        fontStyle: erPseudonym ? 'italic' : 'normal',
      })
      .setOrigin(0, 0);
    container.add(author);
    nestY += 56;

    const badges = this.buildBadges(CARD_PADDING, nestY, manus);
    container.add(badges);
    nestY += 84;

    const blurb = this.scene.add
      .text(CARD_PADDING, nestY, `«${manus.editorial_blurb}»`, {
        fontFamily: FONTS.body,
        fontSize: fontStr(24),
        color: '#1a1a1a',
        fontStyle: 'italic',
        wordWrap: { width: CARD_WIDTH - CARD_PADDING * 2 },
      })
      .setOrigin(0, 0);
    container.add(blurb);

    const buttonsY = cardHeight - 92;

    if (harBy && manus.konkurrentBy) {
      this.buildKnapperMedBy(container, buttonsY, manus, snap);
    } else {
      this.buildKnapperUtenBy(container, buttonsY, manus, snap);
    }

    return container;
  }

  private buildKnapperUtenBy(
    container: Phaser.GameObjects.Container,
    buttonsY: number,
    manus: Manuscript,
    snap: GameStateData,
  ): void {
    const buttonWidth = (CARD_WIDTH - CARD_PADDING * 2 - 32) / 3;

    const avslaaBtn = this.makeButton(
      CARD_PADDING,
      buttonsY,
      buttonWidth,
      'Avslå',
      'outline',
      () => gameState.avslaaManus(manus.isbn),
    );
    container.add(avslaaBtn);

    const tilbakeBtn = this.makeButton(
      CARD_PADDING + buttonWidth + 16,
      buttonsY,
      buttonWidth,
      'Send tilbake',
      'secondary',
      () => gameState.sendTilbake(manus.isbn),
    );
    container.add(tilbakeBtn);

    const forskudd = FORSKUDD_PER_TIER[manus.tier];
    const harRaad = snap.kroner >= forskudd;
    const tilbyLabel = harRaad
      ? `Tilby (${formaterKr(forskudd)})`
      : 'For lite kapital';
    const tilbyBtn = this.makeButton(
      CARD_PADDING + (buttonWidth + 16) * 2,
      buttonsY,
      buttonWidth,
      tilbyLabel,
      'primary',
      () => this.openConfirm(manus),
      !harRaad,
    );
    container.add(tilbyBtn);
  }

  private buildKnapperMedBy(
    container: Phaser.GameObjects.Container,
    buttonsY: number,
    manus: Manuscript,
    snap: GameStateData,
  ): void {
    const by = manus.konkurrentBy!;
    const konkurrent = konkurrentKatalog.findById(by.konkurrentId);
    const navn = konkurrent?.spillNavn ?? 'Konkurrent';
    const matchPris = by.beloep + 1;

    // 4 knapper i 2 rader
    const radH = 78;
    const radGap = 14;
    const halvBredde = (CARD_WIDTH - CARD_PADDING * 2 - 16) / 2;

    // Rad 1: Avslå · Send tilbake
    const avslaaBtn = this.makeButton(
      CARD_PADDING,
      buttonsY - radH - radGap,
      halvBredde,
      'Avslå',
      'outline',
      () => gameState.avslaaManus(manus.isbn),
    );
    container.add(avslaaBtn);

    const tilbakeBtn = this.makeButton(
      CARD_PADDING + halvBredde + 16,
      buttonsY - radH - radGap,
      halvBredde,
      'Send tilbake',
      'secondary',
      () => gameState.sendTilbake(manus.isbn),
    );
    container.add(tilbakeBtn);

    // Rad 2: Match og tilby · La X ta den
    const harRaad = snap.kroner >= matchPris;
    const matchLabel = harRaad
      ? `Match og tilby (${formaterKr(matchPris)})`
      : 'For lite kapital til match';
    const matchBtn = this.makeButton(
      CARD_PADDING,
      buttonsY,
      halvBredde,
      matchLabel,
      'primary',
      () => this.openConfirmMatch(manus, matchPris, navn),
      !harRaad,
    );
    container.add(matchBtn);

    const laGaaBtn = this.makeButton(
      CARD_PADDING + halvBredde + 16,
      buttonsY,
      halvBredde,
      `La ${navn} ta den (−2 omdømme)`,
      'secondary',
      () => gameState.avslaaKonkurrentBy(manus.isbn),
    );
    container.add(laGaaBtn);
  }

  private buildForfatterBanner(
    x: number,
    y: number,
    width: number,
    forfatterNavn: string,
  ): { container: Phaser.GameObjects.Container; height: number } {
    const c = this.scene.add.container(x, y);
    const padX = 16;
    const padY = 12;

    const prefix = this.scene.add
      .text(padX, padY, '✨ Nytt manus fra din forfatter ', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(22),
        color: '#3a2410',
        fontStyle: '700',
      })
      .setOrigin(0, 0);
    const navn = this.scene.add
      .text(padX + prefix.width, padY, forfatterNavn, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(22),
        color: '#3a2410',
        fontStyle: 'italic',
      })
      .setOrigin(0, 0);
    const h = Math.max(prefix.height, navn.height) + padY * 2;
    const bg = this.scene.add
      .rectangle(0, 0, width, h, COLORS.yellowWarm, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.oakDark, 0.4);
    c.add([bg, prefix, navn]);
    return { container: c, height: h };
  }

  private buildKonkurrentBanner(
    x: number,
    y: number,
    width: number,
    konkurrentId: string,
    beloep: number,
  ): { container: Phaser.GameObjects.Container; height: number } {
    const c = this.scene.add.container(x, y);
    const padX = 16;
    const padY = 12;
    const konk = konkurrentKatalog.findById(konkurrentId);
    const navn = konk?.spillNavn ?? 'Konkurrent';

    const tekst = this.scene.add
      .text(
        padX,
        padY,
        `⚠  ${navn} har bydd ${formaterKr(beloep)} for dette manuset.`,
        {
          fontFamily: FONTS.hud,
          fontSize: fontStr(22),
          color: '#3a2410',
          fontStyle: '700',
          wordWrap: { width: width - padX * 2 },
        },
      )
      .setOrigin(0, 0);
    const h = tekst.height + padY * 2;
    const bg = this.scene.add
      .rectangle(0, 0, width, h, COLORS.yellowWarm, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.oakDark, 0.6);
    c.add([bg, tekst]);
    return { container: c, height: h };
  }

  private buildBadges(x: number, y: number, manus: Manuscript): Phaser.GameObjects.Container {
    const row = this.scene.add.container(x, y);

    const genreBg = this.scene.add
      .rectangle(0, 0, 0, 0, COLORS.oakLight, 1)
      .setOrigin(0, 0);
    const genreText = this.scene.add
      .text(12, 8, manus.genre, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(20),
        color: '#3a2410',
        fontStyle: '600',
      })
      .setOrigin(0, 0);
    const gW = genreText.width + 24;
    const gH = genreText.height + 16;
    genreBg.setSize(gW, gH);
    row.add([genreBg, genreText]);

    const tierColor = TIER_COLORS[manus.tier];
    const tierBg = this.scene.add
      .rectangle(gW + 12, 0, 0, 0, tierColor, 1)
      .setOrigin(0, 0);
    const tierText = this.scene.add
      .text(gW + 12 + 12, 8, `Tier ${manus.tier}`, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(20),
        color: '#ffffff',
        fontStyle: '700',
      })
      .setOrigin(0, 0);
    const tW = tierText.width + 24;
    tierBg.setSize(tW, gH);
    row.add([tierBg, tierText]);

    return row;
  }

  private makeButton(
    x: number,
    y: number,
    width: number,
    label: string,
    variant: 'primary' | 'secondary' | 'outline',
    onClick: () => void,
    disabled = false,
  ): Phaser.GameObjects.Container {
    const container = this.scene.add.container(x, y);
    const height = 72;

    let fill: number = 0xffffff;
    let textColor = '#862736';
    let stroke: number = COLORS.wine;
    let strokeWidth = 3;
    let shadowAlpha = 0;

    if (variant === 'primary') {
      fill = COLORS.wine;
      textColor = '#ffffff';
      strokeWidth = 0;
      shadowAlpha = 0.32;
    } else if (variant === 'secondary') {
      fill = COLORS.oakLight;
      textColor = '#3a2410';
      stroke = COLORS.oakDark;
      strokeWidth = 0;
      shadowAlpha = 0.18;
    } else {
      shadowAlpha = 0.12;
    }

    if (disabled) {
      fill = 0xcfc6b7;
      textColor = '#7a7060';
      strokeWidth = 0;
      shadowAlpha = 0.08;
    }

    if (shadowAlpha > 0) {
      const shadow = this.scene.add
        .rectangle(2, 4, width, height, 0x000000, shadowAlpha)
        .setOrigin(0, 0);
      container.add(shadow);
    }

    const bg = this.scene.add
      .rectangle(0, 0, width, height, fill, 1)
      .setOrigin(0, 0);
    if (strokeWidth > 0) bg.setStrokeStyle(strokeWidth, stroke, 1);
    container.add(bg);

    const txt = this.scene.add
      .text(width / 2, height / 2, label, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(22),
        color: textColor,
        fontStyle: '600',
        align: 'center',
        wordWrap: { width: width - 16 },
      })
      .setOrigin(0.5);
    container.add(txt);

    if (!disabled) {
      bg.setInteractive({ useHandCursor: true });
      bg.on('pointerover', () => {
        bg.setAlpha(0.92);
        if (variant === 'primary') {
          sfx.play('click');
        }
      });
      bg.on('pointerout', () => bg.setAlpha(1));
      bg.on('pointerup', onClick);
    }

    return container;
  }

  private openConfirm(manus: Manuscript): void {
    this.closeConfirm();
    this.confirmLayer = this.scene.add.container(0, 0).setDepth(200);

    const backdrop = this.scene.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.6)
      .setOrigin(0, 0)
      .setInteractive();
    this.confirmLayer.add(backdrop);

    const dW = 880;
    const dH = 520;
    const dX = (GAME_WIDTH - dW) / 2;
    const dY = (GAME_HEIGHT - dH) / 2;

    const sheet = this.scene.add
      .rectangle(dX, dY, dW, dH, COLORS.white, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(3, COLORS.wine, 0.8);
    this.confirmLayer.add(sheet);

    const title = this.scene.add
      .text(dX + dW / 2, dY + 48, 'Tilby kontrakt?', {
        fontFamily: FONTS.display,
        fontSize: fontStr(46),
        color: '#862736',
      })
      .setOrigin(0.5, 0);
    this.confirmLayer.add(title);

    const forskudd = FORSKUDD_PER_TIER[manus.tier];

    const body = this.scene.add
      .text(
        dX + dW / 2,
        dY + 156,
        `«${manus.title}»\nav ${manus.author}\n\nTier ${manus.tier} · Forskudd: ${formaterKr(forskudd)}`,
        {
          fontFamily: FONTS.body,
          fontSize: fontStr(26),
          color: '#1a1a1a',
          align: 'center',
          wordWrap: { width: dW - 64 },
        },
      )
      .setOrigin(0.5, 0);
    this.confirmLayer.add(body);

    const btnY = dY + dH - 120;
    const btnW = 320;
    const gap = 32;
    const totalBtnW = btnW * 2 + gap;
    const startX = dX + (dW - totalBtnW) / 2;

    const avbrytBtn = this.makeButton(startX, btnY, btnW, 'Avbryt', 'outline', () =>
      this.closeConfirm(),
    );
    this.confirmLayer.add(avbrytBtn);

    const jaBtn = this.makeButton(startX + btnW + gap, btnY, btnW, 'Ja, tilby', 'primary', () => {
      const ok = gameState.tilbyKontrakt(manus.isbn);
      this.closeConfirm();
      if (!ok) return;
      sfx.play('bell');
    });
    this.confirmLayer.add(jaBtn);
  }

  private openConfirmMatch(manus: Manuscript, pris: number, konkurrentNavn: string): void {
    this.closeConfirm();
    this.confirmLayer = this.scene.add.container(0, 0).setDepth(200);

    const backdrop = this.scene.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.6)
      .setOrigin(0, 0)
      .setInteractive();
    this.confirmLayer.add(backdrop);

    const dW = 880;
    const dH = 580;
    const dX = (GAME_WIDTH - dW) / 2;
    const dY = (GAME_HEIGHT - dH) / 2;

    const sheet = this.scene.add
      .rectangle(dX, dY, dW, dH, COLORS.white, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(3, COLORS.wine, 0.8);
    this.confirmLayer.add(sheet);

    const title = this.scene.add
      .text(dX + dW / 2, dY + 48, 'Match og tilby?', {
        fontFamily: FONTS.display,
        fontSize: fontStr(46),
        color: '#862736',
      })
      .setOrigin(0.5, 0);
    this.confirmLayer.add(title);

    const body = this.scene.add
      .text(
        dX + dW / 2,
        dY + 156,
        `«${manus.title}»\nav ${manus.author}\n\n${konkurrentNavn} har bydd ${formaterKr(
          pris - 1,
        )}.\nDu betaler ${formaterKr(pris)} — én krone over.`,
        {
          fontFamily: FONTS.body,
          fontSize: fontStr(24),
          color: '#1a1a1a',
          align: 'center',
          wordWrap: { width: dW - 64 },
        },
      )
      .setOrigin(0.5, 0);
    this.confirmLayer.add(body);

    const btnY = dY + dH - 120;
    const btnW = 320;
    const gap = 32;
    const totalBtnW = btnW * 2 + gap;
    const startX = dX + (dW - totalBtnW) / 2;

    const avbrytBtn = this.makeButton(startX, btnY, btnW, 'Avbryt', 'outline', () =>
      this.closeConfirm(),
    );
    this.confirmLayer.add(avbrytBtn);

    const jaBtn = this.makeButton(
      startX + btnW + gap,
      btnY,
      btnW,
      'Ja, match',
      'primary',
      () => {
        gameState.matchKonkurrentBy(manus.isbn);
        this.closeConfirm();
        sfx.play('bell');
      },
    );
    this.confirmLayer.add(jaBtn);
  }

  private closeConfirm(): void {
    this.confirmLayer?.destroy(true);
    this.confirmLayer = undefined;
  }
}

function formaterKr(n: number): string {
  return `${n.toLocaleString('nb-NO')} kr`;
}
